import type { ReactNode } from 'react'
import type React from 'react'
import { Button, Card, Select, Space, Switch, Tag } from 'antd'
import { resolveWidgetWidthCssValue } from '../builderCore'
import { AgentBuilderWidgetPanel } from '../components/AgentBuilderWidgetPanel'
import type { WidgetPresentationState } from '../vizModel'
import { createEmptyDashboardDateRange, findDatePresetOption, getEffectiveSlicerPanel } from '../runtime'
import { formatPresetDate, formatPresetWindow, groupDatePresetOptions } from '../datePresets'
import type {
  DashboardCatalogPlatform,
  DashboardDefinition,
  DashboardRuntimeDateRange,
  DashboardRuntimeState,
  DatePresetOption,
} from '../types'
import { createWidgetSessionState, type DashboardGlobalFilterState } from './engine/state'
import type { DashboardViewEngine, useDashboardFilterBehaviour } from './dashboardControls'


// Preset values always start with "_" followed by letters/digits/spaces, so these
// sentinels cannot collide with a catalog preset.
const CUSTOM_DATE_OPTION = '__custom__'
const NO_DATE_FILTER_OPTION = '__none__'

type DateSelectOption = { value: string; label: string }
type DateSelectGroup = { label: string; title: string; options: DateSelectOption[] }

function buildDateSelectOptions(
  datePresetOptions: DatePresetOption[],
  selectedPreset: string | null,
): Array<DateSelectOption | DateSelectGroup> {
  const result: Array<DateSelectOption | DateSelectGroup> = [
    { value: CUSTOM_DATE_OPTION, label: 'Custom' },
    { value: NO_DATE_FILTER_OPTION, label: 'No date filter' },
  ]

  // A stored preset is provisional until the catalog loads (an unlisted one then becomes
  // custom), so keep it selected meanwhile without showing its raw column name.
  if (selectedPreset && !findDatePresetOption(datePresetOptions, selectedPreset)) {
    result.push({ value: selectedPreset, label: 'Loading…' })
  }

  for (const group of groupDatePresetOptions(datePresetOptions)) {
    result.push({
      label: group.label,
      title: group.label,
      options: group.options.map((option) => ({ value: option.value, label: option.label })),
    })
  }

  return result
}

function toDateSelectValue(dateRange: DashboardRuntimeDateRange) {
  if (dateRange.mode === 'preset') return dateRange.preset ?? undefined
  return dateRange.mode === 'none' ? NO_DATE_FILTER_OPTION : CUSTOM_DATE_OPTION
}

function arePageFilterStatesEqual(left: DashboardGlobalFilterState, right: DashboardGlobalFilterState) {
  return JSON.stringify(left) === JSON.stringify(right)
}

function ViewerSlicerPanel({
  dashboard,
  draftFilters,
  appliedFilters,
  datePresetOptions,
  datePresetsAsOf,
  slicerOptionsByField,
  slicerOptionsLoadingByField,
  onSlicerChange,
  onDateRangeChange,
  onApplyFilters,
  onClearAll,
  autoApply,
  onAutoApplyChange,
}: {
  dashboard: DashboardDefinition
  draftFilters: DashboardGlobalFilterState
  appliedFilters: DashboardGlobalFilterState
  datePresetOptions: DatePresetOption[]
  datePresetsAsOf: string | null
  slicerOptionsByField: Record<string, string[]>
  slicerOptionsLoadingByField: Record<string, boolean>
  onSlicerChange: (field: string, value: string | string[] | null) => void
  onDateRangeChange: (dateRange: DashboardRuntimeDateRange) => void
  onApplyFilters: () => void
  onClearAll: () => void
  autoApply: boolean
  onAutoApplyChange: (next: boolean) => void
}) {
  const slicerPanel = getEffectiveSlicerPanel(dashboard)
  const slicers = slicerPanel?.slicers ?? []
  const dateRange = slicerPanel?.dateRange
  const hasPendingChanges = !arePageFilterStatesEqual(draftFilters, appliedFilters)
  const draftDateRange = draftFilters.dateRange
  const selectedPreset = draftDateRange.mode === 'preset' ? draftDateRange.preset : null
  const selectedPresetOption = findDatePresetOption(datePresetOptions, selectedPreset)
  // For a preset the inputs show the model's resolved window (an output); for
  // custom they are the user's own dates; for "none" they are empty.
  const displayedFrom = draftDateRange.mode === 'preset'
    ? selectedPresetOption?.from ?? null
    : draftDateRange.mode === 'custom' ? draftDateRange.from : null
  const displayedTo = draftDateRange.mode === 'preset'
    ? selectedPresetOption?.to ?? null
    : draftDateRange.mode === 'custom' ? draftDateRange.to : null
  const isPresetWindow = draftDateRange.mode === 'preset'
  const dateSelectOptions = buildDateSelectOptions(datePresetOptions, selectedPreset)

  const handleDateSelect = (value: string) => {
    if (value === NO_DATE_FILTER_OPTION) {
      onDateRangeChange(createEmptyDashboardDateRange())
      return
    }
    if (value === CUSTOM_DATE_OPTION) {
      // Start the custom range from whatever window is currently shown.
      onDateRangeChange({ mode: 'custom', preset: null, from: displayedFrom, to: displayedTo })
      return
    }
    onDateRangeChange({ mode: 'preset', preset: value, from: null, to: null })
  }

  // Typing a date always drops to a custom range, keeping the other end as shown.
  const handleDateInput = (key: 'from' | 'to', value: string | null) => {
    onDateRangeChange({
      mode: 'custom',
      preset: null,
      from: key === 'from' ? value : displayedFrom,
      to: key === 'to' ? value : displayedTo,
    })
  }

  if (slicers.length === 0 && !dateRange) {
    return null
  }

  return (
    <Card className="border-0 shadow-sm">
      <div className="space-y-3">
        {dateRange ? (
          <div className="flex flex-col gap-3 border-b border-dashed border-gray-200 pb-3 sm:flex-row sm:flex-wrap sm:items-center">
            <div className="flex w-full flex-col gap-0.5 text-gray-600 sm:w-auto sm:min-w-[150px]">
              <span className="text-[13px] font-semibold text-gray-700">Date Range</span>
            </div>
            <label className="flex w-full flex-col text-gray-600 sm:w-auto sm:min-w-[240px]">
              <span className="sr-only">Period</span>
              <Select
                size="middle"
                value={toDateSelectValue(draftDateRange)}
                onChange={handleDateSelect}
                options={dateSelectOptions}
                placeholder="Select period"
                showSearch
                optionFilterProp="label"
                popupMatchSelectWidth={false}
                listHeight={360}
                aria-label="Date range period"
                className="w-full [&_.ant-select-selector]:!h-[34px] [&_.ant-select-selector]:!px-3 [&_.ant-select-selection-placeholder]:!leading-[32px]"
              />
            </label>
            <label className="flex w-full flex-col text-gray-600 sm:w-auto">
              <span className="sr-only">From</span>
              <input
                type="date"
                value={displayedFrom ?? ''}
                onChange={(event) => handleDateInput('from', event.target.value || null)}
                title={isPresetWindow ? 'Set by the selected period. Change it to use a custom range.' : undefined}
                aria-label="From date"
                className={`h-[34px] w-full rounded-md border border-gray-300 px-3 text-xs sm:w-auto ${isPresetWindow ? 'bg-gray-50 text-gray-500' : 'bg-white text-gray-700'}`}
              />
            </label>
            <label className="flex w-full flex-col text-gray-600 sm:w-auto">
              <span className="sr-only">To</span>
              <input
                type="date"
                value={displayedTo ?? ''}
                onChange={(event) => handleDateInput('to', event.target.value || null)}
                title={isPresetWindow ? 'Set by the selected period. Change it to use a custom range.' : undefined}
                aria-label="To date"
                className={`h-[34px] w-full rounded-md border border-gray-300 px-3 text-xs sm:w-auto ${isPresetWindow ? 'bg-gray-50 text-gray-500' : 'bg-white text-gray-700'}`}
              />
            </label>
            {isPresetWindow && selectedPresetOption ? (
              <span className="w-full text-[12px] text-gray-500 sm:w-auto" data-testid="date-preset-as-of">
                {[
                  formatPresetWindow(selectedPresetOption),
                  datePresetsAsOf
                    ? `as of ${formatPresetDate(datePresetsAsOf)}`
                    : 'as of the last data refresh',
                ].filter(Boolean).join(', ')}
              </span>
            ) : null}
          </div>
        ) : null}

        {slicers.length > 0 ? (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-6">
            {slicers.map((slicer) => {
              const rawSelected = draftFilters.slicerValues[slicer.field]
              const selectedValues = (Array.isArray(rawSelected) ? rawSelected : rawSelected ? [rawSelected] : [])
                .map((value) => String(value ?? '').trim())
                .filter((value) => value && value.toLowerCase() !== 'all')
              const options = (slicerOptionsByField[slicer.field] ?? [])
                .map((option) => String(option ?? '').trim())
                .filter((option) => option && option.toLowerCase() !== 'all')
              const isLoading = slicerOptionsLoadingByField[slicer.field] ?? false

              const isSingle = slicer.selection === 'single'

              return (
                <label key={slicer.field} className="flex flex-col gap-1 text-gray-600">
                  <span className="text-[11px] font-semibold text-gray-600">{slicer.label}</span>
                  {isSingle ? (
                    <Select
                      size="middle"
                      value={selectedValues[0] ?? undefined}
                      onChange={(value) => onSlicerChange(slicer.field, value ?? null)}
                      options={options.map((option) => ({ value: option, label: option }))}
                      placeholder={isLoading ? 'Loading...' : 'Select value'}
                      allowClear
                      className="w-full [&_.ant-select-selector]:!min-h-[34px] [&_.ant-select-selector]:!px-3 [&_.ant-select-selection-placeholder]:!leading-[32px]"
                    />
                  ) : (
                    <Select
                      size="middle"
                      mode="multiple"
                      value={selectedValues}
                      onChange={(value) => onSlicerChange(slicer.field, value.length > 0 ? value : null)}
                      options={options.map((option) => ({ value: option, label: option }))}
                      placeholder={isLoading ? 'Loading...' : 'Select values'}
                      allowClear
                      className="w-full [&_.ant-select-selector]:!min-h-[34px] [&_.ant-select-selector]:!px-3 [&_.ant-select-selection-overflow]:!flex-nowrap [&_.ant-select-selection-placeholder]:!leading-[32px]"
                      maxTagCount="responsive"
                    />
                  )}
                </label>
              )
            })}
          </div>
        ) : null}

        <div className="flex justify-start sm:justify-end">
          <Space wrap>
            <span className="flex items-center gap-2 text-[12px] text-gray-600">
              <Switch size="small" checked={autoApply} onChange={onAutoApplyChange} />
              Auto-apply filters
            </span>
            <Tag color={hasPendingChanges ? 'gold' : 'green'} className="!text-xs">
              {hasPendingChanges ? 'Pending changes' : 'Filters applied'}
            </Tag>
            <Button size="small" onClick={onClearAll}>
              Clear all
            </Button>
            <Button
              size="small"
              type="primary"
              onClick={onApplyFilters}
              disabled={!hasPendingChanges || autoApply}
            >
              Apply filters
            </Button>
          </Space>
        </div>
      </div>
    </Card>
  )
}


type DashboardBodyProps = {
  clientId: string
  engine: DashboardViewEngine
  dashboard: DashboardDefinition
  catalog: DashboardCatalogPlatform[] | null
  datePresetOptions: DatePresetOption[]
  datePresetsAsOf: string | null
  runtimeState: DashboardRuntimeState
  selectedFilters: Record<string, string | string[]>
  filters: ReturnType<typeof useDashboardFilterBehaviour>
  /** Between the slicers and the widgets: the app's AI insights, or a note where there are none. */
  insights?: ReactNode
  /** Across the bottom of each widget. */
  widgetFooter?: ReactNode
  /** False hides the date range and slicers; the saved ones still apply to the data. */
  showSlicers?: boolean
  hideTrendGranularity?: boolean
  onWidgetPresentationChange?: (widgetId: string, state: WidgetPresentationState) => void
}

/** The dashboard itself: slicers and widgets, with no app chrome. Shared by the app's viewer and the Claude viewer. */
export function DashboardBody({
  clientId,
  engine,
  dashboard,
  catalog,
  datePresetOptions,
  datePresetsAsOf,
  runtimeState,
  selectedFilters,
  filters,
  insights,
  widgetFooter,
  showSlicers = true,
  hideTrendGranularity = false,
  onWidgetPresentationChange,
}: DashboardBodyProps) {
  return (
    <>
      {showSlicers && (
        <ViewerSlicerPanel
          dashboard={dashboard}
          draftFilters={engine.draftFilters}
          appliedFilters={engine.appliedFilters}
          datePresetOptions={datePresetOptions}
          datePresetsAsOf={datePresetsAsOf}
          slicerOptionsByField={engine.slicerOptionsByField}
          slicerOptionsLoadingByField={engine.slicerLoadingByField}
          onSlicerChange={engine.actions.changeDraftSlicer}
          onDateRangeChange={engine.actions.changeDraftDateRange}
          onApplyFilters={() => void engine.actions.applyGlobalFilters()}
          onClearAll={filters.clearAll}
          autoApply={filters.autoApply}
          onAutoApplyChange={filters.setAutoApply}
        />
      )}

      {insights ? <div>{insights}</div> : null}

      <div className="dashboard-print-widget-grid -m-2 flex flex-col md:flex-row md:flex-wrap">
        {dashboard.widgets.filter((widget) => widget.type !== 'insight').map((widget) => (
          <div
            key={widget.id}
            className="dashboard-print-widget-wrap w-full p-2 md:[flex:0_0_var(--agent-builder-widget-width)] md:[max-width:var(--agent-builder-widget-width)]"
            style={{ '--agent-builder-widget-width': resolveWidgetWidthCssValue(widget) } as React.CSSProperties}
          >
            <AgentBuilderWidgetPanel
              clientId={clientId}
              dashboardId={engine.dashboardId}
              dashboard={dashboard}
              widget={widget}
              session={engine.widgetSessions[widget.id] ?? createWidgetSessionState(widget)}
              catalog={catalog}
              dashboardFilters={dashboard.dashboardFilters ?? []}
              selectedFilters={selectedFilters}
              runtimeState={runtimeState}
              onSort={engine.actions.setWidgetSort}
              onPage={(widgetId, page) => { void engine.actions.setWidgetPage(widgetId, page) }}
              onDimensionChange={(widgetId, groupByDimension) => { void engine.actions.setWidgetDimension(widgetId, groupByDimension) }}
              onMeasureParameterChange={(widgetId, selections) => { void engine.actions.setWidgetMeasureParameter(widgetId, selections) }}
              onPickerStateChange={engine.actions.setWidgetPickerState}
              onDrilldown={(widgetId, drilldown) => { void engine.actions.setWidgetDrilldown(widgetId, drilldown) }}
              onClearDrilldown={(widgetId) => { void engine.actions.clearWidgetDrilldown(widgetId) }}
              onPresentationStateChange={onWidgetPresentationChange}
              mode="viewer"
              footer={widgetFooter}
              hideTrendGranularity={hideTrendGranularity}
            />
          </div>
        ))}
      </div>
    </>
  )
}
